import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, Send, Loader2, MessageCircle, CalendarCheck, Linkedin } from "lucide-react";
import { Reveal, Chapter } from "@/components/site/Reveal";
import { PARTNERS, GENERAL, BOOKING_URL, FORMAT_OPTIONS, BUDGET_OPTIONS, STAGE_OPTIONS } from "@/data/content";
import { gmailTo, ENQUIRY_SUBJECT, CAREERS_SUBJECT, CAREERS_BODY } from "@/lib/email";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "", company: "", email: "", phone: "",
  production_format: "", budget_range: "", project_stage: "", message: "",
};

const inputCls =
  "w-full border border-white/15 bg-ink/60 px-4 py-3.5 text-sm text-ivory placeholder:text-mist/50 outline-none transition-all duration-300 focus:border-gold focus:bg-ink";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-mist";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className={labelCls}>{label}</label>
    {children}
  </div>
);

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [stamped, setStamped] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.production_format || !form.budget_range || !form.project_stage) {
      toast.error("Please select the format, budget range and project stage.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. A partner will respond within one business day.");
      setForm(INITIAL);
      setStamped(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your enquiry. Please call or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative bg-ink">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute bottom-0 left-1/3 h-[380px] w-[620px] rounded-full bg-gold/[0.06] blur-[140px]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <Chapter n="Chapter 10" label="Begin the Engagement" dark />
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="text-balance font-serif text-3xl font-medium leading-tight tracking-tight text-ivory sm:text-4xl lg:text-5xl">
                Start the <span className="italic text-gold">conversation.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-mist sm:text-lg">
                Tell us about your production — format, stage and scale. A partner will respond
                within one business day with a point of view, not a sales deck.
              </p>
            </Reveal>
            <div className="mt-12 space-y-6">
              {PARTNERS.map((p, i) => (
                <Reveal key={p.id} delay={0.2 + i * 0.1}>
                  <div className="border-l border-gold/30 pl-6">
                    <p className="font-serif text-lg italic text-ivory">{p.name}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-gold">{p.role}</p>
                    <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
                      <a data-testid={`contact-partner-${i + 1}-call`} href={p.phoneHref} className="flex items-center gap-2 text-sm text-ivory/85 transition-colors hover:text-gold">
                        <Phone size={13} strokeWidth={1.5} /> {p.phone}
                      </a>
                      <a data-testid={`contact-partner-${i + 1}-email`} href={gmailTo(p.email, ENQUIRY_SUBJECT)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-ivory/85 transition-colors hover:text-gold">
                        <Mail size={13} strokeWidth={1.5} /> {p.email}
                      </a>
                      <a data-testid={`contact-partner-${i + 1}-whatsapp`} href={p.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-ivory/85 transition-colors hover:text-gold">
                        <MessageCircle size={13} strokeWidth={1.5} /> WhatsApp
                      </a>
                      <a data-testid={`contact-partner-${i + 1}-linkedin`} href={p.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-ivory/85 transition-colors hover:text-gold">
                        <Linkedin size={13} strokeWidth={1.5} /> LinkedIn
                      </a>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.45}>
              <div className="mt-12 border border-gold/30 bg-gradient-to-br from-abyss to-ink p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-gold">Prefer a Fixed Slot?</p>
                <h3 className="mt-4 font-serif text-2xl font-medium text-ivory">Book a 30-minute discovery call.</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-mist">
                  Choose a time that suits your production calendar. A partner — never an account
                  manager — will be on the line.
                </p>
                <a
                  data-testid="book-call-button"
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-7 inline-flex items-center gap-3 border border-gold/60 bg-gold/10 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
                >
                  <CalendarCheck size={15} strokeWidth={1.5} />
                  Book a Call
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.55}>
              <div className="relative mt-6 overflow-hidden border border-white/10 bg-abyss/40 p-8">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute right-5 top-5 rotate-12 border-2 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.24em]"
                  style={{ borderColor: "rgba(200,86,77,0.7)", color: "#C8564D" }}
                >
                  We're Hiring
                </span>
                <p className="font-mono text-[10px] uppercase tracking-[0.26em]" style={{ color: "#C8564D" }}>Careers</p>
                <h3 className="mt-4 font-serif text-2xl font-medium text-ivory">Fresh legs, sharp pencils.</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-mist">
                  Articled assistants, freshly qualified CAs and production-finance trainees — if
                  ledgers and film sets both excite you, drop your resume and tell us why.
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <a
                    data-testid="careers-email-button"
                    href={gmailTo("connect@verivistaadvisory.com", CAREERS_SUBJECT, CAREERS_BODY)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 border border-white/20 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ivory transition-all duration-300 hover:border-gold hover:text-gold"
                  >
                    <Send size={14} strokeWidth={1.5} />
                    Drop Your Resume
                  </a>
                  <a
                    data-testid="careers-whatsapp-button"
                    href={GENERAL.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 border border-gold/60 bg-gold/10 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
                  >
                    <MessageCircle size={14} strokeWidth={1.5} />
                    DM on WhatsApp
                  </a>
                </div>
                <p className="mt-4 font-mono text-[10px] tracking-[0.14em] text-mist/60">
                  connect@verivistaadvisory.com · or simply DM on WhatsApp {GENERAL.phone}
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="lg:col-span-7">
            <form data-testid="enquiry-form" onSubmit={submit} onFocusCapture={() => setStamped(false)} className="relative border border-white/10 bg-abyss/60 p-8 sm:p-10">
              <AnimatePresence>
                {stamped && (
                  <motion.div
                    data-testid="enquiry-received-stamp"
                    className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.4 } }}
                  >
                    <motion.div
                      initial={{ scale: 2.4, rotate: -24, opacity: 0 }}
                      animate={{ scale: 1, rotate: -11, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 320, damping: 15, mass: 1.05 }}
                      className="border-[3px] px-10 py-5 text-center"
                      style={{ borderColor: "rgba(200,86,77,0.85)", boxShadow: "inset 0 0 0 2px rgba(200,86,77,0.4)" }}
                    >
                      <p className="font-mono text-xl uppercase tracking-[0.3em] sm:text-2xl" style={{ color: "#C8564D" }}>
                        Enquiry Received
                      </p>
                      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "rgba(200,86,77,0.8)" }}>
                        Verivista Advisory LLP
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Full Name" htmlFor="name">
                  <input id="name" data-testid="input-name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                </Field>
                <Field label="Company / Production House" htmlFor="company">
                  <input id="company" data-testid="input-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Optional" />
                </Field>
                <Field label="Email" htmlFor="email">
                  <input id="email" data-testid="input-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@studio.com" />
                </Field>
                <Field label="Phone" htmlFor="phone">
                  <input id="phone" data-testid="input-phone" required minLength={6} value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 ..." />
                </Field>
                <Field label="Production Format" htmlFor="format">
                  <select id="format" data-testid="select-format" required value={form.production_format} onChange={set("production_format")} className={inputCls}>
                    <option value="" disabled>Select format</option>
                    {FORMAT_OPTIONS.map((o) => <option key={o} value={o} className="bg-ink">{o}</option>)}
                  </select>
                </Field>
                <Field label="Budget Range" htmlFor="budget">
                  <select id="budget" data-testid="select-budget" required value={form.budget_range} onChange={set("budget_range")} className={inputCls}>
                    <option value="" disabled>Select range</option>
                    {BUDGET_OPTIONS.map((o) => <option key={o} value={o} className="bg-ink">{o}</option>)}
                  </select>
                </Field>
                <Field label="Project Stage" htmlFor="stage">
                  <select id="stage" data-testid="select-stage" required value={form.project_stage} onChange={set("project_stage")} className={inputCls}>
                    <option value="" disabled>Select stage</option>
                    {STAGE_OPTIONS.map((o) => <option key={o} value={o} className="bg-ink">{o}</option>)}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="About the Project" htmlFor="message">
                    <textarea id="message" data-testid="input-message" required minLength={10} rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Timelines, locations, current controls — anything that helps us prepare." />
                  </Field>
                </div>
              </div>
              <button
                data-testid="form-submit-button"
                type="submit"
                disabled={sending}
                className="group mt-9 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.24em] text-ink transition-all duration-300 hover:bg-gold-bright disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />}
                {sending ? "Submitting..." : "Submit Project Enquiry"}
              </button>
              <p className="mt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-mist/70">
                Confidential. Shared only with the partners. No retainers pitched on the first call.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
